import type { ReactNode } from "react";
import { CheckCircle2, CircleHelp, Cpu, FileText, HelpCircle, MinusCircle, Paperclip, Scale, Sparkles, XCircle, type LucideIcon } from "lucide-react";
import { MOD, cn } from "@ncp/ui";
import type { AiVerdict, FindingVerdict } from "./api";

/**
 * The two voices of the workbench, told apart by colour everywhere they appear: the platform (rules engine and agents) speaks in sea
 * blue, the officer in gold. Verdict colours (green / red / amber) are shared, so the voice colour is what says whose view it is.
 */
export const voiceMeta = {
  ai: {
    label: "text-seablue-700",
    icon: "text-seablue-600",
    surface: "bg-seablue-50/50",
    border: "border-seablue-200",
    edge: "border-s-seablue-500",
    chip: "border-seablue-200 bg-seablue-100 text-seablue-900",
    heading: "bg-seablue-100 text-seablue-800",
  },
  officer: {
    label: "text-aegold-800",
    icon: "text-aegold-700",
    surface: "bg-aegold-50/30",
    border: "border-aegold-200",
    edge: "border-s-aegold-500",
    chip: "border-aegold-200 bg-aegold-100 text-aegold-900",
    heading: "bg-aegold-100 text-aegold-800",
  },
} as const;

/** Icon, colour and badge tone of every verdict — the AI's and the officer's share one vocabulary. */
export const verdictMeta: Record<AiVerdict | FindingVerdict, { icon: LucideIcon; tone: string; badge: "success" | "danger" | "warning" | "outline" | "info"; ring: string }> = {
  Satisfied: { icon: CheckCircle2, tone: "text-success-600", badge: "success", ring: "border-success-400 bg-success-50 text-success-800" },
  Deficient: { icon: XCircle, tone: "text-danger-600", badge: "danger", ring: "border-danger-400 bg-danger-50 text-danger-800" },
  NeedsInformation: { icon: HelpCircle, tone: "text-warning-600", badge: "warning", ring: "border-warning-400 bg-warning-50 text-warning-900" },
  NotApplicable: { icon: MinusCircle, tone: "text-sand-500", badge: "outline", ring: "border-sand-400 bg-sand-100 text-sand-800" },
  Unknown: { icon: CircleHelp, tone: "text-sand-400", badge: "outline", ring: "border-sand-300 bg-white text-sand-600" },
  Pending: { icon: CircleHelp, tone: "text-sand-400", badge: "outline", ring: "border-sand-300 bg-white text-sand-600" },
};

/** The record-and-next shortcut as the officer's keyboard labels it. */
export const submitKeys = MOD === "⌘" ? "⌘↵" : "Ctrl+Enter";

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border border-sand-300 bg-sand-50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-sand-700">{children}</kbd>;
}

export function SourceIcon({ kind, className }: { kind: string; className?: string }) {
  const Icon: LucideIcon = kind === "Document" ? FileText : kind === "Proposal" ? Sparkles : kind === "Tool" || kind === "Clock" || kind === "Task" ? Cpu : kind === "Citation" ? Scale : Paperclip;
  return <Icon className={cn("size-3 shrink-0 text-sand-600", className)} />;
}

export function SectionHeading({ icon: Icon, children, tone = "sand" }: { icon: LucideIcon; children: ReactNode; tone?: "sand" | "ai" | "officer" }) {
  const box = tone === "ai" ? voiceMeta.ai.heading : tone === "officer" ? voiceMeta.officer.heading : "bg-sand-100 text-sand-700";
  const text = tone === "ai" ? voiceMeta.ai.label : tone === "officer" ? voiceMeta.officer.label : "text-sand-700";
  return (
    <span className="flex items-center gap-2">
      <span className={cn("grid size-6 shrink-0 place-items-center rounded-md", box)}><Icon className="size-3.5" /></span>
      <span className={cn("text-xs font-semibold uppercase tracking-wider", text)}>{children}</span>
    </span>
  );
}

/** A checklist item pinned as a source is identified by `checklist:<key>`; the workbench can resolve it in place. */
export function checklistKeyOf(sourceId: string): string | null {
  return sourceId.startsWith("checklist:") ? sourceId.slice("checklist:".length) : null;
}

/** A version number as it appears everywhere in the workbench. */
export function VersionTag({ version, className }: { version: number; className?: string }) {
  return <span className={cn("rounded bg-aeblack-950 px-1 py-px font-mono text-[11px] font-semibold text-white", className)}>v{version}</span>;
}

/** Colour of a source's status word (paid, missing, proposed…) so a list of sources reads at a glance. */
export function statusTone(status: string): string {
  const s = status.toLowerCase();
  if (["paid", "present", "approved", "edited", "sent", "delivered", "completed", "met", "verified", "admissible", "replied", "notified", "minutesapproved", "closed", "responded", "held", "match"].includes(s)) return "bg-success-100 text-success-800";
  if (["missing", "deficient", "unsupported", "rejected", "breached", "notmet", "inadmissible", "failed", "notnotified", "waived", "namemismatch", "notfound", "inactive", "overdue"].includes(s)) return "bg-danger-100 text-danger-800";
  if (["proposed", "pending", "awaiting", "draft", "open", "paused", "atrisk", "repliedlate", "nominutes", "inreview"].includes(s)) return "bg-warning-100 text-warning-900";
  return "bg-sand-100 text-sand-700";
}
